import { answerInquiry, getInquiry, mapClientError, queryKeys } from "@dutyon/apis";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  LoadingOverlay,
  notifications,
  SafeHtml,
  Stack,
  Text,
  Textarea,
  Title,
  TitleContainer,
} from "@dutyon/ui-kit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { ChevronLeft } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

export function InquiryDetailPage() {
  const { id: routeId } = useParams<{ id: string }>();
  const id = routeId ?? "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [answer, setAnswer] = useState("");

  const { data: inquiry, isLoading } = useQuery({
    queryKey: queryKeys.inquiries.detail(id),
    queryFn: () => getInquiry(id),
    enabled: Boolean(id),
  });

  const mutation = useMutation({
    mutationFn: (data: { answer: string }) => answerInquiry(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inquiries.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.inquiries.all });
      setAnswer("");
    },
    onError: (error) => {
      notifications.show({
        title: "답변 등록 실패",
        message: mapClientError(error, { fallback: "문의 답변을 등록하지 못했습니다." }).message,
        color: "red",
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.inquiries.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.inquiries.all });
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!answer.trim()) return;
    mutation.mutate({ answer });
  };

  if (isLoading) {
    return (
      <TitleContainer
        size="lg"
        title={
          <Group gap="xs">
            <ActionIcon variant="subtle" color="gray" onClick={() => navigate("/inquiries")}>
              <ChevronLeft />
            </ActionIcon>
            <Title order={3}>문의 상세</Title>
          </Group>
        }
      >
        <LoadingOverlay visible />
      </TitleContainer>
    );
  }

  if (!inquiry) {
    return (
      <TitleContainer
        size="lg"
        title={
          <Group gap="xs">
            <ActionIcon variant="subtle" color="gray" onClick={() => navigate("/inquiries")}>
              <ChevronLeft />
            </ActionIcon>
            <Title order={3}>문의 상세</Title>
          </Group>
        }
      >
        <Text>존재하지 않는 문의사항입니다.</Text>
      </TitleContainer>
    );
  }

  return (
    <TitleContainer
      size="lg"
      title={
        <Group gap="xs">
          <ActionIcon variant="subtle" color="gray" onClick={() => navigate("/inquiries")}>
            <ChevronLeft />
          </ActionIcon>
          <Title order={3}>문의 상세</Title>
        </Group>
      }
    >
      <Stack gap="lg">
        {/* 문의 정보 */}
        <Card withBorder p="lg" radius="md">
          <Stack gap="md">
            <Group justify="space-between" align="flex-start">
              <div>
                <Group gap="sm" mb="xs">
                  <Badge size="lg" color={inquiry.status === "ANSWERED" ? "green" : "gray"}>
                    {inquiry.status === "ANSWERED" ? "답변완료" : "답변대기"}
                  </Badge>
                </Group>
                <Text size="sm">
                  {inquiry.product === "PRO" ? "Pro" : "Enterprise"} ·{" "}
                  {
                    {
                      GENERAL: "사용법·기타",
                      GENERATION: "생성 실패",
                      BILLING: "결제·환불",
                      ACCOUNT: "계정",
                    }[inquiry.category ?? "GENERAL"]
                  }
                </Text>
                <Title order={4}>{inquiry.title}</Title>
              </div>
              <Stack gap={4} align="flex-end">
                <Text size="sm" fw={500}>
                  작성자: {inquiry.user?.name}
                </Text>
                <Text size="xs" c="dimmed">
                  {dayjs(inquiry.createdAt).format("YYYY년 MM월 DD일 HH:mm")}
                </Text>
              </Stack>
            </Group>

            {inquiry.product === "PRO" && (
              <Stack gap="xs">
                {inquiry.proOrderId && (
                  <Text size="sm" style={{ overflowWrap: "anywhere" }}>
                    관련 주문: {inquiry.proOrderId}
                  </Text>
                )}
                {inquiry.proScheduleId && (
                  <Text size="sm" style={{ overflowWrap: "anywhere" }}>
                    관련 근무표: {inquiry.proScheduleId}
                  </Text>
                )}
                {inquiry.proGenerationJobId && (
                  <Text size="sm" style={{ overflowWrap: "anywhere" }}>
                    관련 생성 작업: {inquiry.proGenerationJobId}
                  </Text>
                )}
              </Stack>
            )}
            <Divider />

            <div>
              <Text size="sm" c="dimmed" mb="xs">
                문의 내용
              </Text>
              <Card withBorder p="md" radius="sm" bg="gray.0">
                {inquiry.product === "PRO" ? (
                  <Text style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                    {inquiry.content}
                  </Text>
                ) : (
                  <SafeHtml html={inquiry.content} />
                )}
              </Card>
            </div>
          </Stack>
        </Card>

        {/* 답변 영역 */}
        <Card withBorder p="lg" radius="md">
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={700} size="lg">
                답변
              </Text>
              {inquiry.answer && inquiry.updatedAt && (
                <Text size="xs" c="dimmed">
                  답변일: {dayjs(inquiry.updatedAt).format("YYYY년 MM월 DD일 HH:mm")}
                </Text>
              )}
            </Group>

            {inquiry.answer ? (
              <Card withBorder p="md" radius="sm" bg="blue.0">
                {inquiry.product === "PRO" ? (
                  <Text style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                    {inquiry.answer}
                  </Text>
                ) : (
                  <SafeHtml html={inquiry.answer} />
                )}
              </Card>
            ) : (
              <form onSubmit={onSubmit}>
                <Stack>
                  <Textarea
                    placeholder="답변을 입력하세요"
                    minRows={8}
                    required
                    value={answer}
                    onChange={(e) => setAnswer(e.currentTarget.value)}
                  />
                  <Group justify="flex-end">
                    <Button type="submit" loading={mutation.isPending} leftSection={<span>✓</span>}>
                      답변 등록
                    </Button>
                  </Group>
                </Stack>
              </form>
            )}
          </Stack>
        </Card>
      </Stack>
    </TitleContainer>
  );
}
